import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import {
  ApiResponse_searchResponseDTO_1,
  ApiResponse_string,
  ApiResponse_userDTO,
  ApiResponse_userEditDTO,
  ForgotPasswordDTO,
  ResetPasswordDTO,
  SearchRequestDTO_userSearchRequestDTO,
  UserDTO,
  UserClient,
  ApiResponse_list_1,
  ApiResponse_boolean,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

interface UseUserApi {
  create: (data: UserDTO) => Promise<ApiResponse_userDTO>;
  edit: (id: string, data: UserDTO) => Promise<ApiResponse_userEditDTO>;
  getById: (id: string) => Promise<ApiResponse_userDTO>;
  search: (
    filters: SearchRequestDTO_userSearchRequestDTO,
  ) => Promise<ApiResponse_searchResponseDTO_1>;
  remove: (id: string) => Promise<ApiResponse_string>;
  uploadProfilePicture: (
    fileName: string,
    file: File,
  ) => Promise<ApiResponse_string>;
  forgotPassword: (dto: ForgotPasswordDTO) => Promise<ApiResponse_string>;
  resetPassword: (dto: ResetPasswordDTO) => Promise<ApiResponse_string>;
  getAllPermissions: () => Promise<ApiResponse_list_1>;
  checkEmailExists: (email: string) => Promise<ApiResponse_boolean>;
  checkPhoneNumberExists: (phoneNumber: string) => Promise<ApiResponse_boolean>;
  isLoading: boolean;
  error: string | null;
}

export const useUserApi = (): UseUserApi => {
  const apiClient = new UserClient(API_BASE_URL, axiosInstance);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (data: UserDTO): Promise<ApiResponse_userDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.createUser(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error creating User', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const edit = async (
    id: string,
    data: UserDTO,
  ): Promise<ApiResponse_userEditDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.updateUser(id, data);
    } catch (err: unknown) {
      handleHookError(err, 'Error updating User', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getById = async (id: string): Promise<ApiResponse_userDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.getUserById(id);
    } catch (err: unknown) {
      handleHookError(err, 'Error fetching User', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const search = async (
    filters: SearchRequestDTO_userSearchRequestDTO,
  ): Promise<ApiResponse_searchResponseDTO_1> => {
    setIsLoading(true);
    try {
      return await apiClient.searchUsers(filters);
    } catch (err: unknown) {
      handleHookError(err, 'Error searching Users', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const remove = async (id: string): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.deleteUser(id);
    } catch (err: unknown) {
      handleHookError(err, 'Error deleting User', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const uploadProfilePicture = async (
    fileName: string,
    file: File,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.uploadProfilePicture(fileName, file);
    } catch (err: unknown) {
      handleHookError(err, 'Error uploading profile picture', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (
    dto: ForgotPasswordDTO,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.forgotPassword(dto);
    } catch (err: unknown) {
      handleHookError(err, 'Error sending forgot password email', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (
    dto: ResetPasswordDTO,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.resetPassword(dto);
    } catch (err: unknown) {
      handleHookError(err, 'Error resetting password', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getAllPermissions = async (): Promise<ApiResponse_list_1> => {
    setIsLoading(true);
    try {
      return await apiClient.getAllPermissions();
    } catch (err: unknown) {
      handleHookError(err, 'Error getting permissions', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const checkEmailExists = async (
    email: string,
  ): Promise<ApiResponse_boolean> => {
    setIsLoading(true);
    try {
      return await apiClient.checkEmailExists(email);
    } catch (err: unknown) {
      handleHookError(err, 'Error checking if email exists', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const checkPhoneNumberExists = async (
    phoneNumber: string,
  ): Promise<ApiResponse_boolean> => {
    setIsLoading(true);
    try {
      return await apiClient.checkPhoneNumberExists(phoneNumber);
    } catch (err: unknown) {
      handleHookError(err, 'Error checking if phone number exists', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    create,
    edit,
    getById,
    search,
    remove,
    uploadProfilePicture,
    forgotPassword,
    resetPassword,
    getAllPermissions,
    checkEmailExists,
    checkPhoneNumberExists,
    isLoading,
    error,
  };
};

// Helper to get UserClient instance
const getUserClient = () => new UserClient(API_BASE_URL, axiosInstance);

/**
 * TanStack Query hook for fetching all admin users for dropdowns.
 * Searches with role='Admin' and a large page size.
 */
export const useAdminUsers = () =>
  useQuery({
    queryKey: ['adminUsers'],
    queryFn: async () => {
      const response = await getUserClient().searchUsers({
        filters: { role: 'Admin' },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'FirstName',
        sortDescending: false,
      });
      return (response?.data?.results as UserDTO[]) || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

/**
 * TanStack Query hook for fetching profile picture SAS URL
 * - Cached for 23 hours (SAS URLs expire in 24 hours)
 * - Automatically refetches when URL approaches expiry
 * - Invalidate with: queryClient.invalidateQueries(['profilePictureSas', userId])
 */
export const useProfilePictureSasUrl = (userId: string | undefined | null) =>
  useQuery({
    queryKey: ['profilePictureSas', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await getUserClient().generateProfilePictureSas(userId);
      return response?.data || null;
    },
    enabled: !!userId,
    staleTime: 23 * 60 * 60 * 1000, // 23 hours (SAS expires in 24 hours, refresh before expiry)
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours even when unused
    refetchOnWindowFocus: false, // Don't refetch on every focus (we have staleTime)
    refetchOnReconnect: true, // Refetch on reconnect in case SAS expired
  });
